import { motion } from "framer-motion";
import {
  Landmark, PiggyBank, HandCoins, Users, ArrowDownLeft, ArrowUpRight,
  ShieldAlert, DatabaseBackup, CheckCircle2,
} from "lucide-react";
import { EASE } from "../../lib/motion";

const METRICS = [
  { label: "Balance general", value: "RD$ 24,850,320", icon: Landmark },
  { label: "Ahorros", value: "RD$ 18,430,210", icon: PiggyBank },
  { label: "Cartera de préstamos", value: "RD$ 32,540,120", icon: HandCoins },
  { label: "Socios activos", value: "4,821", icon: Users },
];

const SERIES = [34, 39, 36, 45, 49, 46, 55, 60, 56, 66, 72, 79];
const W = 520, H = 170, PAD = 10;
const PTS = SERIES.map((v, i) => [
  PAD + (i * (W - 2 * PAD)) / (SERIES.length - 1),
  H - PAD - (v / 100) * (H - 2 * PAD),
]);
const LINE_PATH = PTS.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
const AREA_PATH = `${LINE_PATH} L${PTS[PTS.length - 1][0]},${H - PAD} L${PTS[0][0]},${H - PAD} Z`;

const BARS = [42, 58, 50, 66, 74, 62, 80, 88];

const TRANSACTIONS = [
  { label: "Depósito de ahorros", meta: "hace 2 min", amount: "+RD$ 45,500", pos: true },
  { label: "Desembolso de préstamo", meta: "hace 18 min", amount: "-RD$ 120,000", pos: false },
  { label: "Pago de cuota", meta: "hace 41 min", amount: "+RD$ 8,240", pos: true },
  { label: "Interés de certificado", meta: "hace 1 h", amount: "+RD$ 12,875", pos: true },
  { label: "Transferencia entre cuentas", meta: "hace 2 h", amount: "-RD$ 3,100", pos: false },
];

const ALERTS = [
  { icon: ShieldAlert, tone: "text-amber-400", text: "3 alertas AML por revisar", meta: "Cumplimiento" },
  { icon: DatabaseBackup, tone: "text-emerald-400", text: "Backup automático completado", meta: "02:00 AM" },
  { icon: CheckCircle2, tone: "text-emerald-400", text: "Sincronización contable al día", meta: "CoreBank" },
];

const ESTADO = [
  { label: "Al día", pct: 84, bar: "bg-emerald-400" },
  { label: "En mora", pct: 9, bar: "bg-amber-400" },
  { label: "Refinanciado", pct: 7, bar: "bg-slate-400" },
];

export default function DashboardPreview({ className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 44, rotateX: 10 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.1, ease: EASE }}
      className={`relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A1120] text-slate-100 shadow-[0_40px_120px_-30px_rgba(37,99,235,0.5)] ${className}`}
      data-testid="coopsys-dashboard-preview"
    >
      <div className="pointer-events-none absolute -top-24 left-1/3 h-48 w-72 rounded-full bg-brand/25 blur-[70px]" aria-hidden="true" />

      <div className="relative flex items-center justify-between border-b border-white/10 px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-brand">
            <Landmark className="h-3.5 w-3.5 text-white" />
          </span>
          <span className="font-display text-sm font-bold">
            CoopSys <span className="text-sky-400">CoreBank</span>
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-widest text-slate-500 sm:inline">
            Panel general
          </span>
        </div>
        <span
          className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 font-mono text-[10px] font-semibold tracking-[0.2em] text-amber-300"
          data-testid="demo-badge"
        >
          DEMO
        </span>
      </div>

      <div className="relative grid grid-cols-2 gap-2.5 p-4 lg:grid-cols-4 lg:gap-3">
        {METRICS.map((m, i) => (
          <motion.div
            key={m.label}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 + i * 0.09, duration: 0.6, ease: EASE }}
            className="rounded-xl border border-white/10 bg-white/[0.04] p-3"
          >
            <div className="flex items-center gap-1.5 text-[9px] font-medium uppercase tracking-wider text-slate-400">
              <m.icon className="h-3 w-3 shrink-0 text-sky-400" />
              {m.label}
            </div>
            <p className="num mt-1.5 whitespace-nowrap font-display text-[15px] font-bold sm:text-base" data-testid={`dashboard-metric-${i}`}>
              {m.value}
            </p>
          </motion.div>
        ))}
      </div>

      <div className="relative grid gap-3 px-4 pb-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-300">Evolución de cartera</p>
            <div className="flex items-center gap-3 text-[10px] text-slate-400">
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-sky-400" /> Préstamos</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> Ahorros</span>
            </div>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 h-36 w-full" role="img" aria-label="Gráfico demostrativo de cartera de préstamos">
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((f) => (
              <line key={f} x1={PAD} x2={W - PAD} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            ))}
            <motion.path
              d={AREA_PATH}
              fill="url(#areaGrad)"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.9 }}
            />
            <motion.path
              d={LINE_PATH}
              fill="none"
              stroke="#38BDF8"
              strokeWidth="2.5"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, delay: 0.5, ease: "easeInOut" }}
            />
            <motion.circle
              cx={PTS[PTS.length - 1][0]}
              cy={PTS[PTS.length - 1][1]}
              r="4.5"
              fill="#38BDF8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 2 }}
              className="animate-pulse-dot"
            />
          </svg>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-xs font-semibold text-slate-300">Ahorros mensuales</p>
          <div className="mt-4 flex h-36 items-end gap-2.5">
            {BARS.map((b, i) => (
              <motion.div
                key={i}
                className="flex-1 rounded-t-md bg-gradient-to-t from-cyan-500/30 to-cyan-400/80"
                initial={{ height: 0 }}
                whileInView={{ height: `${b}%` }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 + i * 0.07, duration: 0.7, ease: EASE }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="relative grid gap-3 px-4 pb-4 lg:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 lg:col-span-2">
          <p className="text-xs font-semibold text-slate-300">Últimas transacciones</p>
          <ul className="mt-3 divide-y divide-white/5">
            {TRANSACTIONS.map((t, i) => (
              <motion.li
                key={t.label}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 + i * 0.08, duration: 0.5, ease: EASE }}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <span className="flex items-center gap-2 text-[11px] text-slate-300">
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${t.pos ? "bg-emerald-400/10 text-emerald-400" : "bg-slate-400/10 text-slate-400"}`}>
                    {t.pos ? <ArrowDownLeft className="h-2.5 w-2.5" /> : <ArrowUpRight className="h-2.5 w-2.5" />}
                  </span>
                  <span className="whitespace-nowrap">{t.label}</span>
                  <span className="hidden whitespace-nowrap font-mono text-[9px] text-slate-500 sm:inline">· {t.meta}</span>
                </span>
                <span className={`num whitespace-nowrap text-xs font-semibold ${t.pos ? "text-emerald-400" : "text-slate-300"}`}>{t.amount}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-xs font-semibold text-slate-300">Estado de préstamos</p>
            <div className="mt-3 flex flex-col gap-2.5">
              {ESTADO.map((e, i) => (
                <div key={e.label}>
                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                    <span>{e.label}</span>
                    <span className="num">{e.pct}%</span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      className={`h-full rounded-full ${e.bar}`}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${e.pct}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 1 + i * 0.12, duration: 0.8, ease: EASE }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-xs font-semibold text-slate-300">Alertas</p>
            <ul className="mt-2.5 flex flex-col gap-2">
              {ALERTS.map((a) => (
                <li key={a.text} className="flex items-start gap-2 text-[11px] leading-snug text-slate-300">
                  <a.icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${a.tone}`} />
                  <span className="flex-1">{a.text}</span>
                  <span className="whitespace-nowrap font-mono text-[9px] text-slate-500">{a.meta}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-between border-t border-white/10 px-5 py-3">
        <span className="font-mono text-[10px] text-slate-500">CoopSys CoreBank · Datos exclusivamente demostrativos</span>
        <span className="flex items-center gap-1.5 font-mono text-[10px] text-emerald-400">
          <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-400" /> En línea
        </span>
      </div>
    </motion.div>
  );
}
